// THE PRO FORMA'S PERIOD LABEL — one definition, both ends.
//
// 206E §4.3 [RULED 2026-09-23] — `{periodCovered}` printed `periodDisplay`, a
// text the pro forma window builds with month names typed in English and
// stores; a Dutch client's document read "March 2026". The invoice row keeps
// the inputs (`periodMode`, `periodFrom`, `periodTo`), so the label can be
// built at generation in the DOCUMENT's language — and the builder moves here
// so the server and the window read ONE definition, the way
// `src/lib/folderName.mjs` is read by server/src/lib/m365.js.
//
// 206E §4.3 (3 of 5) — AND IT SPEAKS A LANGUAGE: the month names come from the
// platform's calendar data for that language (`Intl.DateTimeFormat`, the app's
// own locales — en-GB, fr-BE, nl-BE, utils.ts `_LOCALE_BY_LANG`), the dates in
// that locale's numeric form (DD/MM/YYYY in all three). The server passes the
// DOCUMENT's language at generation; the window passes the READER's, and shows
// and stores what it gets. A date is read from its "YYYY-MM-DD" parts and
// formatted in UTC, so no time zone on either end can move it by a day.
// 207 §11 [RULED] — THE QUARTER, PER LANGUAGE: « T2 2026 » in French,
// « K2 2026 » in Dutch, « Q2 2026 » in English, THROUGH THE DICTIONARY
// (`proforma.period.quarter_short`, « Q{n} » / « T{n} » / « K{n} »). This file
// reads no dictionary — it is pure, and read at both ends — so the caller
// hands it the quarter's word: the server the DOCUMENT's (docGen.js
// `docGenWording`), the window the READER's (its own `t`). A caller that
// hands nothing gets today's « Q{n} ».

const LOCALE_BY_LANG = { en: "en-GB", fr: "fr-BE", nl: "nl-BE" }; // BCP-47 tags for Intl — the utils.ts / i18n.tsx twin, exempted beside them

/** "YYYY-MM-DD" → [year, month0, day], from a string or a Date; null when unreadable. */
function partsOf(v) {
  const iso = v instanceof Date ? (Number.isNaN(v.getTime()) ? "" : v.toISOString().slice(0, 10)) : String(v || "");
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
}

/**
 * The inputs are the window's `form` fields (untyped) or the invoice row's:
 * `mode` is `periodMode` ("exact" | "custom" | "month" | "quarter"), `from` /
 * `to` the period's first and last day ("YYYY-MM-DD", or a Date), `lang` a
 * language code (nl / fr / en; anything else reads as English); `quarter`
 * the quarter's word in that language, from the dictionary (207 §11).
 * @param {unknown} mode
 * @param {unknown} from
 * @param {unknown} to
 * @param {unknown} lang
 * @param {(n: number) => string} [quarter]
 * @returns {string}
 */
export function periodLabel(mode, from, to, lang, quarter = (n) => `Q${n}`) {
  const a = partsOf(from);
  const b = partsOf(to);
  if (!a || !b) return "—";
  const locale = LOCALE_BY_LANG[lang === "fr" || lang === "nl" ? lang : "en"];
  const day = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
  const monthName = new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" });
  const fd = (p) => day.format(Date.UTC(p[0], p[1], p[2]));
  const mn = (p) => monthName.format(Date.UTC(p[0], p[1], 1));
  if (mode === "exact" || mode === "custom") {
    return a.join("-") === b.join("-") ? fd(a) : `${fd(a)} — ${fd(b)}`;
  }
  if (mode === "month") {
    if (a[1] === b[1] && a[0] === b[0]) return `${mn(a)} ${a[0]}`;
    if (a[0] === b[0]) return `${mn(a)} — ${mn(b)} ${a[0]}`;
    return `${mn(a)} ${a[0]} — ${mn(b)} ${b[0]}`;
  }
  if (mode === "quarter") {
    const qFrom = Math.floor(a[1] / 3) + 1;
    const qTo = Math.floor(b[1] / 3) + 1;
    if (qFrom === qTo && a[0] === b[0]) return `${quarter(qFrom)} ${a[0]}`;
    if (a[0] === b[0]) return `${quarter(qFrom)} — ${quarter(qTo)} ${a[0]}`;
    return `${quarter(qFrom)} ${a[0]} — ${quarter(qTo)} ${b[0]}`;
  }
  return "—";
}

/** The shapes the builder knows — a stored row with any other mode prints its stored text. */
export const PERIOD_MODES = ["exact", "custom", "month", "quarter"];
